import { ConversationStatus } from '@provision/conversation-types';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import {
  ConversationType,
  conversationTypeOptions,
  getStatusColor,
  getUrgencyColor,
  getUrgencyIcon,
  statusDisplayPropsMap,
} from 'src/graphql';
import { TextCell } from 'src/pages/CustomViews/custom-views-entities-table';
import { useDisplayTime } from 'src/utils/hooks';

export const TypeField = ({ type }: { type: string }) => {
  const text = useMemo(
    () =>
      conversationTypeOptions.find((i) => i.id === (type as ConversationType))?.label ?? '',
    [type],
  );

  return <TextCell text={text} />;
};

export const ConversationStatusField = ({
  statusId,
  snoozeUntil,
}: {
  statusId: string;
  snoozeUntil?: string | null;
}) => {
  const { displayTime } = useDisplayTime();
  const status = statusId as keyof typeof statusDisplayPropsMap;
  const data = statusDisplayPropsMap[status] ?? statusDisplayPropsMap.new;

  const tooltipText = useMemo(() => {
    if (status === ConversationStatus.Snoozed && snoozeUntil) {
      const snoozeAt = dayjs(snoozeUntil);
      const datePart = snoozeAt.format('MMM D');
      return `Snoozed until ${datePart}, ${displayTime(snoozeAt)}`;
    }

    return status;
  }, [displayTime, snoozeUntil, status]);

  return (
    <Tooltip title={tooltipText}>
      <DynamicIcon
        weight={500}
        name={data.icon}
        sx={{
          color: (theme) => getStatusColor(status, theme),
        }}
      />
    </Tooltip>
  );
};

export const ConversationUrgencyField = ({ urgencyId }: { urgencyId: string }) => {
  if (!urgencyId) {
    return '-';
  }

  const colorPath = getUrgencyColor(urgencyId);

  return (
    <Tooltip title={urgencyId}>
      <DynamicIcon
        name={getUrgencyIcon(urgencyId)}
        sx={{
          color: colorPath,
        }}
      />
    </Tooltip>
  );
};
